import React, { useEffect, useState } from "react";
import client, { makeGet, makePatch } from "@culturehq/client";
import { Button, Loader, Pagination, Table, Success, Icon } from "@culturehq/components";

import { useProfile } from "app/context";
import { useFetch, usePagination } from "app/hooks";
import { isTarget, t, Trans } from "app/utils";

import { Time } from "components/common/Time";
import LightboxStories from "components/common/gallery/LightboxStories";
import PageHeader from "components/display/PageHeader";
import getUserName from "app/utils/getUserName";
import { useHistory } from "react-router-dom";
import { deleteEntity, updateEntity } from "../mutations";
import StoryPromptTitle from "./StoryPromptTitle";
import { filtersToQuery, queryToOptions } from "../EmbedStoriesSlider";
import StoryFilterBar from "../StoryFilterBar";

const updateStory = updateEntity("stories");
const deleteStory = deleteEntity("stories");

const StoryRow = ({ story, onReviewClicked }) => {
  const { profile } = useProfile();

  return (
    <tr>
      <td className="StoryTable-Details StoryTable-clickable" onClick={() => onReviewClicked(story)}>
        <div className="StoryTable-Details__Thumbnail-Container">
          <img className="StoryTable-Details__Image" src={story.media.thumbnail || story.media.url} alt={story.media.alt || story.question.question} />
          {story.media.mediaType === "video" && <Icon className="StoryTable-Details__PlayIcon" icon="play-no-border" />}
          {story.visuals.length > 1 && <Icon className="StoryTable-Details__ImagesIcon" icon="images" />}
        </div>
      </td>
      <StoryPromptTitle onReviewClicked={onReviewClicked} story={story} />
      <td className="StoryTable-clickable" onClick={() => onReviewClicked(story)}>
        <h4 className="User">{getUserName(story.creator)}</h4>
        {!isTarget(profile.organization.name) && story.creator.companies?.map(company => (
          <div className="icon-building">
            <Icon icon="building" />
            {company.name}
          </div>
        ))}
        <p><Time time={story.createdAt} format="%b %e %Y, %l:%M %p" keepYear /></p>
      </td>
      <td className="Actions-column">
        <Button icon="eye" onClick={() => onReviewClicked(story)}>
          <Trans>Review</Trans>
        </Button>
      </td>
    </tr>
  );
};

const StoriesArchived = ({ match }) => {
  const [page, onPageClick] = usePagination();
  const [activeStory, setActiveStory] = useState();
  const [openModal, setOpenModal] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const history = useHistory();

  const { fetching, json: { stories = [], pagination = {} }, onUpdate: onStoriesUpdate } = useFetch(
    () => {
      const query = filtersToQuery(selectedFilters);
      const params = { ...queryToOptions(query), states: ["archived"], page: page || 1 };
      const [bodySearch] = selectedFilters;
      if (typeof bodySearch === "string") params.bodySearch = bodySearch;
      return makeGet("/stories", params);
    },
    [page, selectedFilters]
  );

  const onUpdate = story => onStoriesUpdate(updateStory(story));
  const onDelete = story => onStoriesUpdate(deleteStory(story));

  useEffect(
    () => {
      const getStory = async id => {
        try {
          const { story } = await client.getStory({ storyId: id });
          if (story.externalState === "archived") {
            setActiveStory(story);
            setOpenModal(true);
          } else {
            history.push("/admin/story-reviews/archived");
          }
        } catch (error) {
          history.push("/admin/story-reviews/archived");
        }
      };

      if (match.params.storyId) {
        getStory(match.params.storyId);
      }
    },
    [history, match.params.storyId]
  );

  const handleReviewClicked = story => {
    setActiveStory(story);
    setOpenModal(true);
  };

  const handleClose = () => {
    if (match.params.storyId) history.push("/admin/story-reviews/archived");
    setOpenModal(false);
  };

  const handleCommentsChange = (story, numberOfComments) => {
    const storyToUpdate = stories.find(oldStory => oldStory.id === story.id);
    onUpdate({ ...storyToUpdate, commentCount: numberOfComments });
  };

  const handleStoryRemove = storyId => {
    const storyToRemove = stories.find(oldStory => oldStory.id === storyId);
    onDelete(storyToRemove);
    setOpenModal(false);
    setActiveStory(undefined);
  };

  const handleStoryCheerToggle = (storyId, cheerForUser, cheersCount) => {
    const storyToUpdate = stories.find(oldStory => oldStory.id === storyId);
    onUpdate({ ...storyToUpdate, cheerForUser, cheersCount });
  };

  const handleStoryChange = goToIndex => {
    let target = goToIndex;

    if (goToIndex === -1) {
      target = stories.length - 1;
    }

    if (goToIndex > stories.length - 1) {
      target = 0;
    }

    setActiveStory(stories[target]);
    setOpenModal(true);
  };

  const onStoryUpdate = updatedStory => {
    onUpdate(updatedStory);
    setActiveStory(updatedStory);
  };

  const onApproveClicked = async ({ id }) => {
    const { story } = await makePatch(`/stories/${id}`, { externalState: "approved" });
    onDelete(story);
  };

  const handleSelectedTags = (updatedOptions, _) => {
    setSelectedFilters(updatedOptions);
  };

  return (
    <div className="padding-left-15px padding-right-15px StoryApprovals">
      <LightboxStories
        className="gallery-lightbox--Story-Review"
        modalIsOpen={openModal}
        modalStyle={{ overlay: { overflow: "hidden" } }}
        activeStory={activeStory}
        currentUserAnswered
        stories={stories}
        onClose={handleClose}
        onCommentsChange={handleCommentsChange}
        onDelete={handleStoryRemove}
        onStoryCheerToggle={handleStoryCheerToggle}
        onStoryUpdate={onStoryUpdate}
        onStoryChange={handleStoryChange}
        internalVersion
        onApproveClicked={onApproveClicked}
      />
      <PageHeader title={t("Stories Archived")}>
        <Trans>
          The stories below are not displayed anywhere, but you can review and mark
          them as approved, pending review or internal only.
          You can always change your choices after.
        </Trans>
      </PageHeader>
      <StoryFilterBar
        creatable
        selectedOptions={selectedFilters}
        handleSelectedTags={handleSelectedTags}
        icon="ios-search-strong"
      />
      <Loader loading={fetching}>
        <Table className="StoryTable">
          <thead>
            <tr>
              <th className="thumb">
                <Trans>Story</Trans>
              </th>
              <th />
              <th className="users">
                <Trans>User</Trans>
              </th>
              <th className="actions">
                <Trans>Actions</Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {stories.map(story => (
              <StoryRow
                key={story.id}
                story={story}
                onReviewClicked={handleReviewClicked}
              />
            ))}
          </tbody>
        </Table>
        {stories.length < 1 && (
          <Success>
            <Trans>
              No more archived stories to review.
            </Trans>
          </Success>
        )}
        <Pagination {...pagination} onClick={onPageClick} />
      </Loader>
    </div>
  );
};

export default StoriesArchived;
